import { selectWarehouse, updateWarehouseActionKeyCabinetGrant } from 'containers/Warehouses/warehousesSlice'
import CheckingLocationPopup from 'components/popups/CheckingLocationPopup'
import OpenOverlay, { KeyCabinetOverlayPhase } from './OpenOverlay'
import { ReactComponent as MagIcon } from 'assets/magDetails.svg'
import { ReactComponent as HelpIcon } from 'assets/helpBig.svg'
import { playSignal, primeSignal } from 'utils/openSignal'
import { useReduxDispatch, useReduxSelector } from 'store'
import BigCircleBtn from 'components/buttons/BigCircleBtn'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import CircleBtn from 'components/buttons/CircleBtn'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import MoreInfoPopup from './MoreInfoPopup'
import { STATUS } from 'utils/constants'
import { selectConfig } from 'appSlice'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'
import './style.scss'

const type = process.env.REACT_APP_TYPE
type KeyCabinetStatus = 'closed' | 'open'

interface Response {
  result: string
  code?: string
  info: string
  error?: string
  type: string
  grant: warehouse.WarehouseActionKeyCabinetGrant
  /** Czas zwolnienia zamka w sekundach (może być ułamkiem), null = nieznany */
  lockReleaseSeconds?: number | null
}

const toReleaseMs = (value: unknown): number | null => {
  const seconds = typeof value === 'string' ? Number(value) : value
  return (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0)
    ? seconds * 1000
    : null
}

const KeyCabinet: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const config = useReduxSelector(selectConfig)
  const [isSending, setSending] = useState(false)
  const [status, setStatus] = useState<KeyCabinetStatus>('closed')
  const [overlay, setOverlay] = useState<KeyCabinetOverlayPhase | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [releaseTotalMs, setReleaseTotalMs] = useState<number | null>(null)
  const [releaseDeadline, setReleaseDeadline] = useState<number | null>(null)
  const [remainingMs, setRemainingMs] = useState(0)
  const { placeAddress, placeShortAddress, placeGps, actions, hasAccess } = useReduxSelector(state => selectWarehouse(state, id))
  const { popup, openPopup } = usePopupState<{ type: 'checkingKeyCabinetLocation' | 'moreInfo' }>()
  const isAvailable = hasAccess && actions.key_cabinet?.grant?.canOpen
  const action = actions?.key_cabinet?.grant?.action
  const grant = actions?.key_cabinet?.grant
  const overlayRef = useRef(overlay)
  overlayRef.current = overlay

  const { push, replace, goBack } = useHistory()
  const { t } = useTranslation('keyCabinet')
  const dispatch = useReduxDispatch()

  const open = async () => {
    if (isSending || !action || action.type !== 'request') return
    setSending(true)
    try {
      const response: Response = await POST(`${urls.api}${action.url}`, { body: action.payload })
      setSending(false)
      if (response.grant) {
        dispatch(updateWarehouseActionKeyCabinetGrant({ id, grant: response.grant }))
      }
      if (response.result === 'ok') {
        const totalMs = toReleaseMs('lockReleaseSeconds' in response
          ? response.lockReleaseSeconds
          : actions?.key_cabinet?.lockReleaseSeconds)
        setStatus('open')
        setReleaseTotalMs(totalMs)
        setRemainingMs(totalMs ?? 0)
        setReleaseDeadline(totalMs !== null ? Date.now() + totalMs : null)
        setErrorMessage(undefined)
        setOverlay('open')
        playSignal('opened')
      } else {
        setErrorMessage(response.error || response.info || undefined)
        setOverlay('error')
        playSignal('error')
      }

    } catch (error) {
      setSending(false)
      playSignal('error')
    }
  }

  const requestOpen = () => {
    primeSignal()
    setOverlay(null)
    if (config?.warehouseLocationCheckEnabled) {
      openPopup({ type: 'checkingKeyCabinetLocation' })
    } else {
      open()
    }
  }

  const closeOverlay = () => {
    setOverlay(null)
    if (releaseDeadline === null) setStatus('closed')
  }

  useEffect(() => {
    if (releaseDeadline === null) return
    const tick = () => {
      const left = Math.max(0, releaseDeadline - Date.now())
      setRemainingMs(left)
      if (left <= 0) {
        window.clearInterval(interval)
        setReleaseDeadline(null)
        setStatus('closed')
        if (overlayRef.current === 'open') {
          setOverlay('closed')
          playSignal('closed')
        }
      }
    }
    const interval = window.setInterval(tick, 100)
    tick()
    return () => window.clearInterval(interval)
  }, [releaseDeadline])

  return (
    <>
      {popup?.type === 'checkingKeyCabinetLocation' && <CheckingLocationPopup
        targetPosition={(Array.isArray(placeGps) && placeGps.length === 2) ? {
          lat: Number(placeGps[0]),
          lng: Number(placeGps[1])
        } : undefined}
        callback={(success) => {
          if (success) {
            open()
          }
          goBack()
        }}
      />}
      {overlay && <OpenOverlay
        phase={overlay}
        totalMs={releaseTotalMs}
        remainingMs={remainingMs}
        purpose={grant?.purpose}
        keyLabel={grant?.keyLabelText}
        errorMessage={errorMessage}
        canOpenAgain={!!isAvailable}
        onOpenAgain={requestOpen}
        onClose={closeOverlay}
      />}
      {popup?.type === 'moreInfo' && actions?.key_cabinet && <MoreInfoPopup
        data={actions.key_cabinet}
      />}

      <div className='keyCabinet entrance warehouse'>
        <div className='entrance__main center'>
          <svg width="220" height="281" viewBox="0 0 220 281" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect y="10.5" width="206" height="270" rx="3" fill="#434343" />
            <rect x="3" y="13.5" width="200" height="264" rx="2" fill="var(--theme-sec-bg)" />
            <path d="M206 13.5L219.5 0V270.5L203.5 280.5L206 13.5Z" fill="#434343" />
            <path d="M206 13.5L219.5 0H25.5L2.5 11H202L206 13.5Z" fill="#434343" />
            <circle cx="20.5" cy="145" r="7.5" fill="#434343" />
            <path d="M104.207 56.939C103.334 56.939 102.446 56.9986 101.545 57.1026C96.7466 57.6587 92.5587 59.5419 89.605 62.2418C86.6375 64.9431 84.8625 68.5597 85.3062 72.387C85.6945 75.7984 87.7746 78.655 90.7283 80.6241C91.0888 79.7644 91.5464 78.974 92.0041 78.3361C89.5634 76.7275 88.0658 74.5503 87.7884 72.0958C87.4695 69.364 88.7175 66.6072 91.2275 64.3108C93.7513 62.013 97.5093 60.281 101.85 59.779C106.19 59.2756 110.253 60.1021 113.235 61.762C116.202 63.4233 118.047 65.8265 118.366 68.5458C118.671 71.2222 117.506 73.9263 115.121 76.1728C113.646 75.2513 111.95 74.7436 110.212 74.7029H109.907C109.199 74.7167 108.506 74.7999 107.785 74.9525C102.585 76.1589 99.3537 81.262 100.546 86.4206C101.281 89.6101 103.542 92.0507 106.398 93.1462L111.903 117.026L118.421 121.061L122.512 114.585L119.253 112.574L121.305 109.329L118.047 107.305L120.085 104.074L116.826 102.049L118.879 98.804L117.007 90.7056C119.101 88.473 120.057 85.2835 119.322 82.0802C118.948 80.4577 118.185 79.0294 117.131 77.8646C119.752 75.2298 121.264 71.8324 120.848 68.2685C120.404 64.4481 117.852 61.3293 114.344 59.3782C111.501 57.7904 107.993 56.914 104.207 56.939ZM101.406 75.729C97.8144 75.8816 94.4863 78.0587 93.0302 81.581C91.7683 84.6179 92.1982 87.9183 93.89 90.4837L84.488 113.087L87.4417 120.159L94.5417 117.261L93.058 113.725L96.608 112.283L95.138 108.747L98.688 107.305L97.2042 103.768L100.754 102.312L103.916 94.7132C101.073 93.1185 98.896 90.3866 98.1056 86.9753C97.1488 82.7736 98.5355 78.5718 101.406 75.729ZM109.449 76.7275C110.683 76.7136 111.848 77.2128 112.694 78.031C111.931 78.5163 111.085 78.9462 110.198 79.3345C109.976 79.2513 109.74 79.2236 109.491 79.2236C109.407 79.2236 109.338 79.2236 109.269 79.2374C108.298 79.3484 107.577 80.0833 107.452 80.9986C107.424 81.1372 107.424 81.2759 107.424 81.4007C107.438 81.4423 107.438 81.4978 107.438 81.5394C107.577 82.7042 108.589 83.5085 109.754 83.3699C110.697 83.2589 111.418 82.5517 111.557 81.6642C112.417 81.2759 113.221 80.846 113.984 80.3607C114.011 80.4993 114.039 80.638 114.053 80.7767C114.344 83.2728 112.527 85.5609 110.031 85.8521C107.549 86.1433 105.247 84.3267 104.97 81.8306C104.679 79.3345 106.495 77.0464 108.991 76.7552C109.144 76.7413 109.296 76.7275 109.449 76.7275Z" fill="#434343" />
          </svg>

          <div
            className='entrance__main__label --bottom'
            onClick={() => {
              window.open(`https://www.google.com/maps/search/?api=1&query=${placeAddress}`)
            }}>
            <i className='icon icon--location-500' />
            <span>{placeShortAddress}</span>
          </div>
        </div>

        <div className='entrance__menu menu__main'>
          <div className='menu__status'>
            <div className='menu__status--margin' />
            <div className='status__options'>
              <div className='status__option'>
                <i className={`icon icon--padlock-${status === 'open' ? 'open' : 'closed'}`} />
                <span>
                  {t(status === 'open' ? 'keyCabinetOpen' : 'keyCabinetClosed')}
                </span>
              </div>
            </div>
          </div>

          <div className='menu__buttons --top'>
            <CircleBtn
              text={t('moreInformation')}
              Icon={HelpIcon}
              action={() => {
                openPopup({ type: 'moreInfo' })
              }} />

            <div className='buttons__col center'>
              <BigCircleBtn
                disabled={!isAvailable}
                text={t(isSending ? 'loading' : status === 'closed' ? 'openKeyCabinet' : 'collectKey')}
                status={isSending ? STATUS.pending : STATUS.idle}
                action={requestOpen}
              />

              <div>
                <CircleBtn
                  text={t('reportProblem')}
                  Icon='problem'
                  action={() => {
                    push(`/help?warehouseId=${id}`)
                  }} />
              </div>
            </div>

            <CircleBtn
              text={t(`manageMag.${type}`)}
              Icon={MagIcon}
              action={() => {
                if (window.location.search.includes('from=mag')) goBack()
                else replace(`/warehouses/${id}`)
              }} />
          </div>
        </div>
      </div>
    </>
  )
}

export default KeyCabinet